import type { KnownDonor } from '../requirements'
import type {
  SurveyAction,
  SurveyAnswer,
  SurveyConfig,
  SurveyOption,
  SurveyQuestion,
} from '../survey'

/** The flows the quiz thank-you runs inside its own card. */
export type InlineFlow = 'single-donation' | 'monthly-membership' | 'raise-amount' | 'blixt'

/** The staff preview level (`previewToken=1|2|3`), only while the host's debug view is on. */
export function previewLevelFrom(debug: boolean, value: unknown): 1 | 2 | 3 | undefined {
  const n = Number(Array.isArray(value) ? value[0] : value)
  return debug && (n === 1 || n === 2 || n === 3) ? n : undefined
}

/** How long after its issue a result token still counts as the visitor's own: an older one on a
 *  shared device may be the person before. */
export const QUIZ_TOKEN_FRESH_SECONDS = 60 * 60

/**
 * A quiz offer's attribution code, for a host's link to its own form. Both forms read it as
 * `?campaignCode`; a membership offer names it `recruitmentId`.
 */
export function quizActionCode(action: SurveyAction): string | undefined {
  const keys =
    action.flow === 'single-donation'
      ? (['campaignCode'] as const)
      : (['recruitmentId', 'campaignCode'] as const)
  for (const key of keys) {
    const value = action.params?.[key]
    if (value !== undefined && value !== '') return String(value)
  }
  return undefined
}

/** The inline flow for a quiz answer, or null to navigate as before. Only a donor the server knows
 *  through the result token gets one: the one-taps send no ids. */
export function inlineFlow(action: SurveyAction, donor: KnownDonor | undefined): InlineFlow | null {
  if (!donor?.hasResultToken) return null
  switch (action.flow) {
    case 'single-donation':
      return 'single-donation'
    // Not 'monthly-donation': that is the EUR card flow, and the quick flow signs a Swedish
    // autogiro with a personnummer.
    case 'monthly-membership':
      return 'monthly-membership'
    // The raise flow uses the token when it reaches level 2, and otherwise asks for BankID first.
    case 'raise-amount':
      return 'raise-amount'
    case 'blixt':
      return 'blixt'
    default:
      return null
  }
}

function optionsOf(q: SurveyQuestion): SurveyOption[] {
  switch (q.type) {
    case 'yes-no':
      return [q.yes, q.no]
    case 'single-select':
    case 'action':
      return q.options
    default:
      return []
  }
}

/** The question an option belongs to. */
export function questionOfOption(config: SurveyConfig, id: string): SurveyQuestion | undefined {
  return config.questions.find((q) => optionsOf(q).some((o) => o.id === id))
}

/** A question's "no": a yes-no's `no`, else the option valued `decline`, else the first option that
 *  launches nothing. What an inline flow's "Nej tack" answers in its place. */
export function declineOption(q: SurveyQuestion): SurveyOption | undefined {
  if (q.type === 'yes-no') return q.no
  const options = optionsOf(q)
  return options.find((o) => o.value === 'decline') ?? options.find((o) => !o.action)
}

/** The address without an inline flow, so a reload doesn't reopen one the person declined. */
export function withoutLaunch(href: string): string {
  const url = new URL(href)
  url.searchParams.delete('launch')
  url.searchParams.delete('donation')
  url.searchParams.delete('qpath')
  return url.toString()
}

export function optionById(config: SurveyConfig, id: string): SurveyOption | undefined {
  for (const q of config.questions) {
    const found = optionsOf(q).find((o) => o.id === id)
    if (found) return found
  }
  return undefined
}

/** The answered option carrying the launched action. Matched by action, not position: re-answering
 *  after going back replaces an answer where it stood. */
export function launchedOption(
  config: SurveyConfig,
  answers: SurveyAnswer[],
  action: SurveyAction,
): SurveyOption | undefined {
  const wanted = JSON.stringify(action)
  for (const answer of answers) {
    const option = answer.optionId ? optionById(config, answer.optionId) : undefined
    if (option?.action && JSON.stringify(option.action) === wanted) return option
  }
  return undefined
}

/** A flow the donor is coming back to (`?launch=<optionId>`), with the payment result if any. */
export function readLaunch(
  config: SurveyConfig,
  search: string,
): {
  option: SurveyOption & { action: SurveyAction }
  payment: 'complete' | 'cancelled' | null
} | null {
  const query = new URLSearchParams(search)
  const option = optionById(config, query.get('launch') ?? '')
  if (!option?.action) return null
  const d = query.get('donation')
  return {
    option: option as SurveyOption & { action: SurveyAction },
    payment: d === 'complete' || d === 'cancelled' ? d : null,
  }
}

/** The address with the launched option in it, so payment return URLs built from it lead back. */
export function withLaunch(href: string, optionId: string): string {
  const url = new URL(href)
  url.searchParams.set('launch', optionId)
  url.searchParams.delete('donation')
  return url.toString()
}

/** The address marked done, so a reload of a finished inline flow doesn't reopen it. */
export function withDone(href: string): string {
  const url = new URL(href)
  url.searchParams.set('donation', 'complete')
  return url.toString()
}

/** The CRM code an answer carries for the form it opens (`params.campaignCode`), if any. */
export function actionCampaignCode(action: SurveyAction): string | undefined {
  const code = action.params?.campaignCode
  return code ? String(code) : undefined
}
